/**
 * AddressSearch - Componente de búsqueda de direcciones
 * 
 * V17 - PTEL Visor Cartográfico
 * 
 * Características:
 * - Input con autocompletado
 * - Lista de candidatos con tipos
 * - Indicador de carga
 * - Resultado seleccionado con coordenadas
 * - Integración con CartoCiudad API
 */

import type { KeyboardEvent } from 'react';
import React, { useRef, useEffect, useCallback } from 'react';
import { Search, MapPin, X, Loader2, Building, Navigation } from 'lucide-react';
import { useAddressSearch, type AddressSearchResult, type AddressCandidate as _AddressCandidate } from './useAddressSearch';
import './AddressSearch.css';

// =============================================================================
// TIPOS
// =============================================================================

export interface AddressSearchProps {
  /** Municipio para filtrar búsquedas */
  municipality?: string;
  /** Provincia para filtrar búsquedas */
  province?: string;
  /** Placeholder del input */
  placeholder?: string;
  /** Posición del componente */
  position?: 'top-left' | 'top-right' | 'top-center';
  /** Callback cuando se selecciona una dirección */
  onSelect?: (result: AddressSearchResult) => void;
  /** Callback para centrar mapa en coordenadas */
  onNavigate?: (x: number, y: number) => void;
  /** Tiempo de debounce en ms */
  debounceMs?: number;
  /** Clases CSS adicionales */
  className?: string;
  /** Mostrar coordenadas del resultado */
  showCoordinates?: boolean;
  /** Ancho del componente */
  width?: string | number;
}

// =============================================================================
// ICONOS POR TIPO
// =============================================================================

const typeIcons: Record<string, React.ReactNode> = {
  portal: <MapPin className="ptel-address-search__type-icon" size={14} />,
  callejero: <Navigation className="ptel-address-search__type-icon" size={14} />,
  municipio: <Building className="ptel-address-search__type-icon" size={14} />,
  provincia: <Building className="ptel-address-search__type-icon" size={14} />,
};

const typeLabels: Record<string, string> = {
  portal: 'Portal',
  callejero: 'Calle',
  municipio: 'Municipio',
  provincia: 'Provincia',
};

// =============================================================================
// COMPONENTE
// =============================================================================

export function AddressSearch({
  municipality,
  province,
  placeholder = 'Buscar dirección...',
  position = 'top-left',
  onSelect,
  onNavigate,
  debounceMs = 300,
  className = '',
  showCoordinates = true,
  width = 320,
}: AddressSearchProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [focusedIndex, setFocusedIndex] = React.useState(-1);

  const {
    query,
    setQuery,
    candidates,
    isLoading,
    error,
    selectedResult,
    selectCandidate,
    clear,
  } = useAddressSearch({
    municipality,
    province,
    debounceMs,
    maxCandidates: 6,
    onSelect,
  });

  // Reset focus cuando cambian candidatos
  useEffect(() => {
    if (candidates.length === 0) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset de índice al cambiar candidatos; guarda explícita
    setFocusedIndex(-1);
  }, [candidates]);

  // Navegación con teclado
  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (candidates.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev < candidates.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev > 0 ? prev - 1 : candidates.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < candidates.length) {
          selectCandidate(candidates[focusedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        clear();
        inputRef.current?.blur();
        break;
    }
  }, [candidates, focusedIndex, selectCandidate, clear]);

  // Scroll al elemento enfocado
  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('.ptel-address-search__candidate');
      items[focusedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex]);

  // Handler para navegar a resultado
  const handleNavigate = useCallback(() => {
    if (selectedResult && onNavigate) {
      onNavigate(selectedResult.x, selectedResult.y);
    }
  }, [selectedResult, onNavigate]);

  // Formatear coordenadas UTM
  const formatCoord = (value: number): string => {
    return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  return (
    <div 
      className={`ptel-address-search ptel-address-search--${position} ${className}`}
      style={{ width: typeof width === 'number' ? `${width}px` : width }}
    >
      {/* Input de búsqueda */}
      <div className="ptel-address-search__input-wrapper">
        <Search 
          className="ptel-address-search__search-icon" 
          size={18} 
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          type="text"
          className="ptel-address-search__input"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          aria-label="Buscar dirección"
          aria-expanded={candidates.length > 0}
          aria-controls="address-candidates-list"
          aria-autocomplete="list"
          autoComplete="off"
        />
        {isLoading && (
          <Loader2 
            className="ptel-address-search__loader" 
            size={18}
            aria-label="Buscando..."
          />
        )}
        {(query || selectedResult) && !isLoading && (
          <button
            className="ptel-address-search__clear-btn"
            onClick={clear}
            aria-label="Limpiar búsqueda"
            type="button"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Lista de candidatos */}
      {candidates.length > 0 && !selectedResult && (
        <ul
          ref={listRef}
          id="address-candidates-list"
          className="ptel-address-search__candidates"
          role="listbox"
          aria-label="Sugerencias de direcciones"
        >
          {candidates.map((candidate, index) => (
            <li
              key={candidate.id}
              className={`ptel-address-search__candidate ${
                index === focusedIndex ? 'ptel-address-search__candidate--focused' : ''
              }`}
              role="option"
              aria-selected={index === focusedIndex}
              onClick={() => selectCandidate(candidate)}
              onMouseEnter={() => setFocusedIndex(index)}
            >
              <span className="ptel-address-search__candidate-icon">
                {typeIcons[candidate.type] || typeIcons.callejero}
              </span>
              <span className="ptel-address-search__candidate-text">
                <span className="ptel-address-search__candidate-address">
                  {candidate.address}
                </span>
                <span className="ptel-address-search__candidate-type">
                  {typeLabels[candidate.type] || candidate.type}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Resultado seleccionado */}
      {selectedResult && (
        <div className="ptel-address-search__result">
          <div className="ptel-address-search__result-header">
            <MapPin size={16} className="ptel-address-search__result-icon" />
            <span className="ptel-address-search__result-address">
              {selectedResult.address}
            </span>
          </div>
          
          {showCoordinates && (
            <div className="ptel-address-search__result-coords">
              <span className="ptel-address-search__coord">
                <span className="ptel-address-search__coord-label">X:</span>
                <span className="ptel-address-search__coord-value">
                  {formatCoord(selectedResult.x)}
                </span>
              </span>
              <span className="ptel-address-search__coord">
                <span className="ptel-address-search__coord-label">Y:</span>
                <span className="ptel-address-search__coord-value">
                  {formatCoord(selectedResult.y)}
                </span>
              </span>
            </div>
          )}

          <div className="ptel-address-search__result-meta">
            <span className="ptel-address-search__confidence">
              Confianza: {selectedResult.confidence}%
            </span>
            {onNavigate && (
              <button
                className="ptel-address-search__navigate-btn"
                onClick={handleNavigate}
                type="button"
              >
                <Navigation size={14} />
                Ir al mapa
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="ptel-address-search__error" role="alert">
          {error}
        </div>
      )}

      {/* Ayuda contextual */}
      {!candidates.length && !selectedResult && !error && query.length > 0 && query.length < 3 && (
        <div className="ptel-address-search__hint">
          Escribe al menos 3 caracteres
        </div>
      )}
    </div>
  );
}

export default AddressSearch;
